import AccountPicker from "../../components/AccountPicker";
import type { Dispatch, SetStateAction } from "react";
import { Trash2 } from "lucide-react";
import type { Account, IOU } from "../../types";
import type { ConfirmState } from "../../components/ConfirmDialog";
import FormActions from "../../components/FormActions";
import DateInput from "../../components/DateInput";
import Modal from "../../components/Modal";
import type { LoanEditDraft, PaymentEditDraft } from "./iou-drafts";

/** Edits a loan's own details, or one repayment against it. Only one of the two is ever open. */
export default function IouEditModal({
  detail, accounts, editingIou, setEditingIou, editingPayment, setEditingPayment,
  error, setError, busy, setConfirm, onSaveLoan, onSavePayment, onRemovePayment,
}: {
  detail: IOU;
  accounts: Account[];
  editingIou: LoanEditDraft | null;
  setEditingIou: Dispatch<SetStateAction<LoanEditDraft | null>>;
  editingPayment: PaymentEditDraft | null;
  setEditingPayment: Dispatch<SetStateAction<PaymentEditDraft | null>>;
  error: string;
  setError: (message: string) => void;
  busy: boolean;
  setConfirm: (confirm: ConfirmState | null) => void;
  onSaveLoan: () => void;
  onSavePayment: () => void;
  onRemovePayment: (settlementId: string) => void;
}) {
  return (
    <Modal title={editingIou ? "Edit loan" : "Edit repayment"} onClose={() => { setEditingIou(null); setEditingPayment(null); setError(""); }}>
      {editingIou && (
        <form className="account-editor" onSubmit={(e) => { e.preventDefault(); onSaveLoan(); }}>
          <label>Description<input required value={editingIou.description} onChange={(e) => setEditingIou({ ...editingIou, description: e.target.value })} /></label>
          <label>Amount<input required inputMode="decimal" value={editingIou.originalAmount} onChange={(e) => setEditingIou({ ...editingIou, originalAmount: e.target.value })} /></label>
          <label><span className="field-label">Due date <small>Optional</small></span><DateInput value={editingIou.dueDate} onChange={(e) => setEditingIou({ ...editingIou, dueDate: e.target.value })} /></label>
          <AccountPicker label="Account" accounts={accounts.filter(a => a.currency === detail.currency)} value={editingIou.fundingAccountId} onChange={(id) => setEditingIou({ ...editingIou, fundingAccountId: id })} required />
          <p className="settings-note">Changing the amount or account moves the loan's own transaction with it; repayments are edited on their own. To change the payee or direction, delete this loan and record it again.</p>
          {error && <div className="inline-error" role="alert">{error}</div>}
          <FormActions label="Save loan" busy={busy} />
        </form>
      )}
      {editingPayment && (
        <form className="account-editor" onSubmit={(e) => { e.preventDefault(); onSavePayment(); }}>
          <label>Amount<input required inputMode="decimal" autoFocus value={editingPayment.amount} onChange={(e) => setEditingPayment({ ...editingPayment, amount: e.target.value })} /></label>
          <label>Date<DateInput required value={editingPayment.date} onChange={(e) => setEditingPayment({ ...editingPayment, date: e.target.value })} /></label>
          <AccountPicker label="Account" accounts={accounts.filter(a => a.currency === detail.currency)} value={editingPayment.accountId} onChange={(id) => setEditingPayment({ ...editingPayment, accountId: id })} required />
          <label><span className="field-label">Notes <small>Optional</small></span><input value={editingPayment.notes} onChange={(e) => setEditingPayment({ ...editingPayment, notes: e.target.value })} /></label>
          <p className="settings-note">The transaction behind this repayment moves with it.</p>
          {error && <div className="inline-error" role="alert">{error}</div>}
          <FormActions
            label="Save repayment"
            busy={busy}
            secondary={
              <button type="button" onClick={() => setConfirm({ title: "Delete this repayment?", message: "The repayment and the transaction behind it will be removed, and the loan will go back up by that amount. This can't be undone.", confirmLabel: "Delete", danger: true, onConfirm: () => onRemovePayment(editingPayment.id) })}><Trash2 size={14} /> Delete repayment</button>
            }
          />
        </form>
      )}
    </Modal>
  );
}
